import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { scrollToId } from "./SmoothScroll";

export const Navbar = () => {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { id: "vision", label: t("nav.vision") },
    { id: "programme", label: t("nav.programme") },
    { id: "biographie", label: t("nav.bio") },
    { id: "adhesion", label: t("nav.adhesion") },
    { id: "contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button data-testid="brand-logo" onClick={() => go("vision")} className="flex items-center gap-3 group">
          <span className="flex items-center justify-center h-11 w-11 rounded-full bg-white border border-slate-200 shadow-sm shrink-0">
            <img src="/prr-logo.png" alt="Logo PRR" className="h-7 w-7 object-contain" />
          </span>
          <span className="flex flex-col leading-none text-left">
            <span className="font-display font-extrabold tracking-tighter text-[15px] text-[#0B1121]">
              Gilles de Buretel de Chassey
            </span>
            <span className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#E1000F] mt-0.5">
              PRR · Reset et Relance
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono-accent text-xs uppercase tracking-widest text-slate-600 hover:text-[#001489] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <LanguageSwitcher />
          <button
            data-testid="nav-cta-soutenir"
            onClick={() => go("soutenir")}
            className="bg-[#0B1121] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#001489] transition-all hover:scale-[1.03] active:scale-95"
          >
            {t("nav.cta")}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-1">
          <LanguageSwitcher testId="language-switcher-mobile" />
          <button data-testid="mobile-menu-toggle" className="text-[#0B1121]" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-black/5 px-5 py-4 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-mono-accent text-xs uppercase tracking-widest text-slate-700"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-nav-cta"
            onClick={() => go("soutenir")}
            className="bg-[#0B1121] text-white text-sm font-semibold px-5 py-3 rounded-full"
          >
            {t("nav.cta")}
          </button>
        </div>
      )}
    </header>
  );
};
